import React, { useState, useEffect } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';
import { X, Sparkles } from 'lucide-react';
import { useToast } from './Toast';
import { confirmMarketingPreview } from './confirmMarketingPreview';
import { notificationsService } from '../services/firebaseService';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../firebase/config';

export const NotificationModal = ({ isOpen, onClose, notification, segments, audience, onSuccess }) => {
  const toast = useToast();
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    icon: '🔔',
    category: 'marketing',
    target_type: 'all',
    target_segment: null,
    scheduled_for: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  const preSelectedSegment = notification?.target_segment || null;
  const isEditMode = !!notification?.id;

  useEffect(() => {
    if (notification) {
      // Backend retourne scheduled_at, on le convertit en format datetime-local pour scheduled_for
      let scheduledFor = '';
      if (notification.scheduled_at) {
        const date = new Date(notification.scheduled_at);
        scheduledFor = new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16); // Format: YYYY-MM-DDTHH:MM
      } else if (notification.scheduled_for) {
        scheduledFor = notification.scheduled_for;
      }

      setFormData({
        title: notification.title || '',
        message: notification.message || '',
        icon: notification.icon || '🔔',
        category: notification.category === 'service' ? 'service' : 'marketing',
        target_type: notification.target_type || (notification.target_segment ? 'segment' : 'all'),
        target_segment: notification.target_segment || notification.target || null,
        scheduled_for: scheduledFor
      });
    } else {
      setFormData({
        title: '',
        message: '',
        icon: '🔔',
        category: 'marketing',
        target_type: 'all',
        target_segment: null,
        scheduled_for: ''
      });
    }
  }, [notification, isOpen]);

  const isService = formData.category === 'service';
  const audienceLabel = (value) => (Number.isFinite(value) ? `${value} appareil(s)` : 'estimation indisponible');

  const handleGenerateWithAI = async () => {
    if (!formData.title || !formData.message) {
      toast.warning('Champs requis', 'Remplis le titre et le message avant de générer avec l\'IA');
      return;
    }

    setGeneratingAI(true);
    try {
      // CLOUD FUNCTION (plus de backend Python)
      const functions = getFunctions(app, 'europe-west1');
      const aiChat = httpsCallable(functions, 'aiChat');

      const prompt = `Améliore ce message de notification pour un restaurant :
Titre: ${formData.title}
Message: ${formData.message}

Consignes:
- Rend le titre plus accrocheur et engageant (max 50 caractères)
- Améliore le message pour être plus persuasif et inciter à l'action (max 150 caractères)
- Garde le même ton et le même sujet
- Réponds UNIQUEMENT au format JSON: {"title": "nouveau titre", "message": "nouveau message"}`;

      const result = await aiChat({ prompt });
      const aiResponse = result.data.response;

      // Tenter de parser la réponse JSON
      try {
        const jsonMatch = aiResponse.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          const improved = JSON.parse(jsonMatch[0]);
          setFormData({
            ...formData,
            title: improved.title || formData.title,
            message: improved.message || formData.message
          });
          toast.success('Texte amélioré !', 'L\'IA a optimisé ton message');
        } else {
          toast.warning('Format inattendu', 'Réponse IA reçue mais format inattendu');
        }
      } catch (parseError) {
        console.error('Error parsing AI response:', parseError);
        toast.warning('Format inattendu', 'Réponse IA reçue mais format inattendu');
      }
    } catch (error) {
      console.error('Error generating with AI:', error);
      toast.error('Erreur IA', error.message);
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleSubmit = async (e, saveDraft = false) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Convertir la date locale en ISO UTC si programmée
      let scheduledAt = null;
      let status = 'draft';

      if (formData.scheduled_for && !saveDraft) {
        const localDate = new Date(formData.scheduled_for);
        scheduledAt = localDate.toISOString();
        status = 'scheduled';
      }

      // Préparer les données
      const category = formData.category === 'service' ? 'service' : 'marketing';
      const dataToSend = {
        title: formData.title,
        message: formData.message,
        type: category,
        category,
        target: category === 'marketing' && formData.target_type === 'segment' ? formData.target_segment : 'all',
        target_ids: [],
        scheduled_at: scheduledAt,
        status: status,
        image_url: null
      };

      // FIREBASE DIRECT (plus de backend Python)
      let result;
      if (isEditMode) {
        result = await notificationsService.update(notification.id, dataToSend);
      } else {
        result = await notificationsService.create(dataToSend);
      }

      // Si pas de date programmée et nouvelle notification, envoyer immédiatement
      const notifId = result.notification?.id || notification?.id;
      if (!saveDraft && !isEditMode && !formData.scheduled_for && notifId) {
        try {
          const sendResult = await notificationsService.proposeAndSend(notifId, confirmMarketingPreview, category);
          if (sendResult.cancelled) toast.success('Brouillon enregistré', `${sendResult.message} — rien n’a été envoyé`);
          else if (sendResult.success) toast.success('Notification acceptée !', sendResult.message);
          else toast.warning('Envoi à vérifier', sendResult.message);
        } catch (sendError) {
          console.error('Error sending notification:', sendError);
          toast.warning('Résultat à vérifier', 'Consultez l’historique avant tout nouvel envoi');
        }
      } else if (formData.scheduled_for && !saveDraft && notifId) {
        const approval = await notificationsService.proposeAndApproveSchedule(notifId, confirmMarketingPreview, category);
        if (!approval.approved) {
          toast.warning('Programmation non approuvée', `${approval.message} — elle ne partira pas`);
          onSuccess?.();
          onClose();
          return;
        }
        const scheduledDate = new Date(formData.scheduled_for);
        const formattedDate = scheduledDate.toLocaleString('fr-FR', {
          dateStyle: 'short',
          timeStyle: 'short'
        });
        toast.success('Programmée !', `Notification prévue pour le ${formattedDate}`);
      } else {
        toast.success(isEditMode ? 'Modifiée !' : 'Sauvegardée !', 'La notification a été enregistrée');
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error saving notification:', error);
      toast.error('Erreur', 'Impossible de sauvegarder la notification');
    } finally {
      setLoading(false);
    }
  };

  const iconOptions = ['🔔', '🎉', '🍔', '🎁', '⚠️', '📢', '✨', '🔥', '💰', '🚀'];

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">
          {isEditMode ? 'Modifier la notification' : 'Créer une notification'}
        </h2>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <X className="w-6 h-6" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Canal : promotion (consentement) ou message de service (tous les appareils) */}
        <fieldset className="space-y-2" disabled={isEditMode}>
          <legend className="block text-sm font-medium mb-2">Type de notification</legend>
          <label className={`flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer ${!isService ? 'border-primary bg-primary/5' : 'border-gray-200'}`}>
            <input type="radio" name="category" checked={!isService}
              onChange={() => setFormData({...formData, category: 'marketing'})} />
            <span className="text-sm">
              <strong>Promotion (clients consentants)</strong>
              <span className="block text-gray-600">Audience estimée : {audienceLabel(audience?.marketing)}</span>
            </span>
          </label>
          <label className={`flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer ${isService ? 'border-primary bg-primary/5' : 'border-gray-200'}`}>
            <input type="radio" name="category" checked={isService}
              onChange={() => setFormData({...formData, category: 'service', target_type: 'all', target_segment: null})} />
            <span className="text-sm">
              <strong>Message de service (tout le monde)</strong>
              <span className="block text-gray-600">Audience estimée : {audienceLabel(audience?.service)}</span>
            </span>
          </label>
          {isService && (
            <p className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
              Message de service : envoyé à tous les appareils ; aucune promotion autorisée.
              Titre 50 caractères et texte 140 caractères maximum, information pratique uniquement.
            </p>
          )}
        </fieldset>

        {/* Icon Selector */}
        <div>
          <label className="block text-sm font-medium mb-2">Icône</label>
          <div className="flex gap-2 flex-wrap">
            {iconOptions.map(icon => (
              <button
                key={icon}
                type="button"
                onClick={() => setFormData({...formData, icon})}
                className={`text-2xl p-2 rounded border-2 ${
                  formData.icon === icon 
                    ? 'border-primary bg-primary/10' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {icon}
              </button>
            ))}
          </div>
        </div>

        {/* Title */}
        <div>
          <label className="block text-sm font-medium mb-2">Titre *</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({...formData, title: e.target.value})}
            className="w-full px-4 py-2 border rounded-lg"
            maxLength={isService ? 50 : undefined}
            placeholder={isService ? 'Mise à jour disponible' : 'Nouvelle offre disponible!'}
            required
          />
        </div>

        {/* Message */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium">Message *</label>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleGenerateWithAI}
              disabled={isService || generatingAI || !formData.title || !formData.message}
              className="text-purple-600 border-purple-300 hover:bg-purple-50"
            >
              <Sparkles className="w-4 h-4 mr-1" />
              {generatingAI ? 'Génération...' : 'Améliorer avec l\'IA'}
            </Button>
          </div>
          <textarea
            value={formData.message}
            onChange={(e) => setFormData({...formData, message: e.target.value})}
            className="w-full px-4 py-2 border rounded-lg"
            rows="4"
            maxLength={isService ? 140 : undefined}
            placeholder={isService ? 'Une nouvelle version de l’app est disponible.' : 'Profitez de 20% de réduction sur tous nos burgers...'}
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            Le message sera envoyé tel quel aux clients
          </p>
        </div>

        {/* Target Type - Only if no pre-selected segment */}
        {!preSelectedSegment && !isService && (
          <div>
            <label className="block text-sm font-medium mb-2">Ciblage</label>
            <select
              value={formData.target_type}
              onChange={(e) => setFormData({...formData, target_type: e.target.value, target_segment: e.target.value === 'all' ? null : formData.target_segment})}
              className="w-full px-4 py-2 border rounded-lg"
            >
              <option value="all">Tous les clients</option>
              <option value="segment">Segment spécifique</option>
            </select>
          </div>
        )}

        {/* Segment Selector */}
        {(formData.target_type === 'segment' || preSelectedSegment) && !preSelectedSegment && (
          <div>
            <label className="block text-sm font-medium mb-2">Sélectionner un segment</label>
            <select
              value={formData.target_segment || ''}
              onChange={(e) => setFormData({...formData, target_segment: e.target.value})}
              className="w-full px-4 py-2 border rounded-lg"
              required
            >
              <option value="">Choisir un segment...</option>
              {segments?.map(seg => (
                <option key={seg.value} value={seg.value}>
                  {seg.label} ({seg.count} clients)
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Pre-selected segment info */}
        {preSelectedSegment && !isService && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Segment ciblé:</strong> {preSelectedSegment}
            </p>
          </div>
        )}

        {/* Scheduled For */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Programmer (optionnel)
          </label>
          <input
            type="datetime-local"
            value={formData.scheduled_for}
            onChange={(e) => setFormData({...formData, scheduled_for: e.target.value})}
            className="w-full px-4 py-2 border rounded-lg"
            min={new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)}
          />
          <p className="text-xs text-gray-500 mt-1">
            Laisser vide pour envoyer immédiatement
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={loading}
            className="flex-1"
          >
            Annuler
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={loading || !formData.title.trim() || !formData.message.trim()}
            onClick={(event) => handleSubmit(event, true)}
            className="flex-1"
          >
            Enregistrer le brouillon
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="flex-1"
          >
            {loading ? 'Enregistrement...' : (
              formData.scheduled_for ? 'Programmer' : (isEditMode ? 'Modifier' : 'Envoyer maintenant')
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
